import ButtonsWrapper, { ButtonsWrapperProps } from '../common/ButtonsWrapper';
import useAppLayout from 'state/hooks/use-app-layout';
import { ReactElement } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { MenuPlacementType } from '@monorepo/shared/src/types';

export type MenurLayoutProps = {
  className?: any;
  primaryLeftButton?: ButtonsWrapperProps;
  secondaryLeftButton?: ButtonsWrapperProps;
  children?: ReactElement;
  onePageTwoPageButtons?: boolean;
  primaryRightButton?: ButtonsWrapperProps;
  secondaryRightButton?: ButtonsWrapperProps;
};

const MenuLayout: React.FC<MenurLayoutProps> = (props) => {
  const {
    className,
    primaryLeftButton,
    primaryRightButton,
    children,
    secondaryLeftButton,
    secondaryRightButton,
  } = props;
  const { menuPlacement, offset } = useAppLayout();
  const classes = styles({ menuPlacement, offset });

  const LeftButtons = () => (
    <>
      {primaryLeftButton && <ButtonsWrapper {...primaryLeftButton} />}
      {secondaryLeftButton && <ButtonsWrapper {...secondaryLeftButton} />}
    </>
  );

  const RightButtons = () => (
    <>
      {secondaryRightButton && <ButtonsWrapper {...secondaryRightButton} />}
      {primaryRightButton && <ButtonsWrapper {...primaryRightButton} />}
    </>
  );

  const TopMenuLayout = () => (
    <header className={`${classes.header} ${className}`}>
      <section className={classes.topLeftContainer}>
        <LeftButtons />
      </section>
      <div className={classes.topCenterContainer}>{children}</div>
      <section className={classes.topRightContainer}>
        <RightButtons />
      </section>
    </header>
  );

  const AsideMenuLayout = () => (
    <>
      <aside className={classes.aside} style={{ left: 0 }}>
        <LeftButtons />
      </aside>
      <aside className={classes.aside} style={{ right: 0 }}>
        <RightButtons />
      </aside>
    </>
  );

  const menuMap = {
    [MenuPlacementType.TOP]: TopMenuLayout,
    [MenuPlacementType.ASIDE]: AsideMenuLayout,
  };

  const Menu = menuMap[menuPlacement];

  return <Menu />;
};

export default MenuLayout;

const styles = makeStyles((theme) => ({
  header: {
    width: '100%',
    position: 'fixed',
    height: theme.typography.pxToRem(theme.constants.app.menuOffset),
    padding: `0 ${theme.typography.pxToRem(theme.constants.app.paddingX)}`,
    zIndex: theme.zIndex.appBar,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    pointerEvents: 'none',
  },
  topLeftContainer: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gridGap: theme.typography.pxToRem(38),
    '& button': {
      pointerEvents: 'auto',
    },
  },
  topCenterContainer: {
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gridGap: '1rem',
    pointerEvents: 'auto',
  },
  topRightContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gridGap: theme.typography.pxToRem(38),
    flex: 1,
    '& button': {
      pointerEvents: 'auto',
    },
  },
  aside: {
    width: theme.typography.pxToRem(theme.constants.app.menuOffset),
    padding: `${theme.typography.pxToRem(theme.constants.app.paddingY)} 0`,
    position: 'fixed',
    zIndex: theme.zIndex.appBar,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gridGap: theme.typography.pxToRem(38),
    '& button': {
      pointerEvents: 'auto',
    },
  },
}));
